import React, { useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SectionLabel, SectionHeading } from './ui/UI';
import { ChevronRight, ArrowRight } from 'lucide-react';
const examples = [{
  title: 'Repository secret → cloud role → production data',
  chain: ['Repository secret', 'Cloud role', 'Production data'],
  body: 'A credential exposure in source code creates access to cloud resources tied to production systems. Periscan shows the source, impact, remediation, rotation guidance, and verification step.'
}, {
  title: 'Identity misconfiguration → privileged access',
  chain: ['Identity misconfig', 'Excessive perms', 'Privileged access'],
  body: 'A conditional access gap, excessive permission, stale admin relationship, or identity path creates privileged access risk. Periscan explains the chain and how to break it.'
}, {
  title: 'External exposure → vulnerable service → sensitive system',
  chain: ['Internet-facing asset', 'Vulnerable service', 'Sensitive system'],
  body: 'An internet-facing asset creates a reachable path toward a high-value system. Periscan validates reachability, business context, and remediation priority.'
}, {
  title: 'AI prompt injection → restricted data retrieval',
  chain: ['User input', 'Prompt injection', 'Restricted data'],
  body: 'An AI app, RAG system, or agent can be manipulated to access data outside the intended policy. Periscan tests the misuse path and produces AI-security evidence.'
}, {
  title: 'Closed ticket → still-exposed risk',
  chain: ['Ticket closed', 'Validation re-run', 'Still exposed'],
  body: 'A remediation ticket was closed, but the exposure still exists or reappeared. Periscan re-tests and flags the gap between activity and actual risk reduction.'
}];
export function AttackPathExamples() {
  const [active, setActive] = useState(0);
  return <section className="relative w-full py-32 border-t border-white/5" data-id="element-159">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-160">
        <SectionLabel number="09" label="Example Paths" className="mb-8" data-id="element-161" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12" data-id="element-162">
          <div className="lg:col-span-8" data-id="element-163">
            <SectionHeading size="xl" data-id="element-164">
              Paths Periscan
              <br data-id="element-165" />
              <span className="text-white/40" data-id="element-166">can identify.</span>
            </SectionHeading>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-167">
          {/* List */}
          <div className="lg:col-span-5" data-id="element-168">
            <div className="space-y-px bg-white/5 rounded-2xl overflow-hidden border border-white/5" data-id="element-169">
              {examples.map((ex, i) => <button key={i} onClick={() => setActive(i)} className={`w-full text-left px-6 py-5 transition-colors flex items-center gap-4 ${active === i ? 'bg-[#1a8fd4]/10' : 'bg-[#070a0e] hover:bg-[#0a0d12]'}`} data-id="element-170">
                  <span className={`font-mono text-xs ${active === i ? 'text-[#1a8fd4]' : 'text-white/30'}`} data-id="element-171">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className={`text-sm font-medium flex-1 ${active === i ? 'text-white' : 'text-white/60'}`} data-id="element-172">
                    {ex.title}
                  </span>
                  <ChevronRight className={`w-4 h-4 transition-all ${active === i ? 'text-[#1a8fd4] translate-x-1' : 'text-white/20'}`} data-id="element-173" />
                </button>)}
            </div>
          </div>

          {/* Detail */}
          <div className="lg:col-span-7" data-id="element-174">
            <AnimatePresence mode="wait" data-id="element-175">
              <motion.div key={active} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.3
            }} className="rounded-2xl bg-gradient-to-br from-[#0a0d12] to-[#070a0e] border border-white/5 p-8 md:p-12" data-id="element-176">
                {/* Chain visualization */}
                <div className="mb-8" data-id="element-177">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[#1a8fd4] mb-6" data-id="element-178">
                    Attack Path
                  </p>
                  <div className="flex flex-wrap items-center gap-3" data-id="element-179">
                    {examples[active].chain.map((node, i) => <Fragment key={i} data-id="element-180">
                        <div className="px-4 py-2.5 rounded-lg bg-[#1a8fd4]/10 border border-[#1a8fd4]/30" data-id="element-181">
                          <span className="font-mono text-xs text-white" data-id="element-182">
                            {node}
                          </span>
                        </div>
                        {i < examples[active].chain.length - 1 && <motion.div initial={{
                      width: 0
                    }} animate={{
                      width: 'auto'
                    }} transition={{
                      duration: 0.5,
                      delay: i * 0.15
                    }} className="flex items-center" data-id="element-183">
                            <ArrowRight className="w-4 h-4 text-[#1a8fd4]/60" data-id="element-184" />
                          </motion.div>}
                      </Fragment>)}
                  </div>
                </div>

                <h3 className="text-2xl md:text-3xl font-semibold text-white mb-4 tracking-tight leading-tight" data-id="element-185">
                  {examples[active].title}
                </h3>
                <p className="text-base text-white/60 leading-relaxed" data-id="element-186">
                  {examples[active].body}
                </p>

                {/* Stats */}
                <div className="mt-8 pt-8 border-t border-white/5 grid grid-cols-3 gap-4" data-id="element-187">
                  {['Evidence', 'Remediation', 'Verification'].map((label, i) => <div key={i} data-id="element-188">
                        <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1" data-id="element-189">
                          {label}
                        </p>
                        <p className="text-sm text-white font-medium" data-id="element-190">
                          Generated
                        </p>
                      </div>)}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>;
}